<script setup lang="ts">
import { Head, Link, router } from '@inertiajs/vue3';
import { Plus } from 'lucide-vue-next';
import { h, ref, watch } from 'vue';
import {
    create as projectCreate,
    index as projectsIndex,
    show as projectShow,
} from '@/actions/App/Http/Controllers/ProjectController';
import DataTable from '@/components/shared/DataTable.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import AppLayout from '@/layouts/AppLayout.vue';
import type { PaginatedResponse, Project } from '@/types';

interface Props {
    projects: PaginatedResponse<Project>;
    filters?: { status?: string };
}

const props = defineProps<Props>();

defineOptions({
    layout: [
        AppLayout,
        { breadcrumbs: [{ title: 'Projets', href: '/projects' }] },
    ],
});

const statusFilter = ref(props.filters?.status || 'all');

watch(statusFilter, (val) => {
    router.get(
        projectsIndex.url(),
        val && val !== 'all' ? { status: val } : {},
        { preserveState: true, replace: true },
    );
});

const statusVariant: Record<string, 'default' | 'secondary' | 'outline'> = {
    active: 'default',
    archived: 'secondary',
    draft: 'outline',
};

const columns = [
    {
        accessorKey: 'name',
        header: 'Nom',
        cell: (info: any) =>
            h(
                Link,
                {
                    href: projectShow.url(info.row.original.id),
                    class: 'font-medium text-brand hover:underline',
                },
                () => info.getValue(),
            ),
    },
    {
        accessorKey: 'status',
        header: 'Statut',
        cell: (info: any) => {
            const status = info.getValue() || 'draft';

            return h(
                Badge,
                {
                    variant: statusVariant[status] ?? 'outline',
                    class: 'capitalize',
                },
                () => status,
            );
        },
    },
    {
        accessorKey: 'papers_count',
        header: 'Articles',
    },
    {
        accessorKey: 'created_at',
        header: 'Créé le',
        cell: (info: any) =>
            new Date(info.getValue()).toLocaleDateString('fr-FR'),
    },
];
</script>

<template>
    <Head title="Projets" />

    <div class="flex flex-col gap-6 p-6">
        <!-- Page header -->
        <div
            class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        >
            <div>
                <h1 class="text-2xl font-bold tracking-tight">Mes Projets</h1>
                <p class="text-muted-foreground">
                    Gérez vos revues de littérature et vos analyses.
                </p>
            </div>
            <Button
                as-child
                class="rounded-lg bg-brand hover:bg-brand/90"
                data-tour="create-project"
            >
                <Link :href="projectCreate.url()">
                    <Plus class="mr-2 size-4" />
                    Nouveau Projet
                </Link>
            </Button>
        </div>

        <!-- Filters -->
        <div class="flex items-center gap-3">
            <Select v-model="statusFilter">
                <SelectTrigger class="w-45 rounded-lg">
                    <SelectValue placeholder="Filtrer par statut" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="active">Actif</SelectItem>
                    <SelectItem value="draft">Brouillon</SelectItem>
                    <SelectItem value="archived">Archivé</SelectItem>
                </SelectContent>
            </Select>
        </div>

        <!-- Table -->
        <div
            class="rounded-lg border border-border bg-card shadow-sm"
            data-tour="projects-list"
        >
            <DataTable
                :columns="columns"
                :data="projects.data"
                search-placeholder="Rechercher un projet..."
            />
        </div>
    </div>
</template>
